import type { Estado, ProjectEntry } from '../content/types';
import { useSrc } from './ContentContext';

export const ESTADO_LABEL: Record<Estado, string> = {
  enPlanos: 'En planos',
  enConstruccion: 'En construcción',
  entregaInmediata: 'Entrega inmediata',
};

/**
 * Tamaño óptico de logos (del diseño): logos anchos bajan de altura y logos cuadrados suben,
 * para que todos tengan un peso visual parecido.
 */
export function logoSize(ratio: number, k: number) {
  const h = Math.min(120, 116 / Math.sqrt(ratio)) * k;
  return { height: Math.round(h), width: Math.round(h * ratio) };
}

export function ProjectLogo({ p, k, onDark = false }: { p: ProjectEntry; k: number; onDark?: boolean }) {
  const src = useSrc();
  if (!p.logo) {
    return (
      <div className={`font-bold leading-tight ${onDark ? 'text-blanco' : 'text-primario'}`} style={{ fontSize: Math.round(44 * k), overflowWrap: 'anywhere' }}>
        {p.nombre}
      </div>
    );
  }
  const { width, height } = logoSize(p.logo.ratio, k);
  return (
    <div
      role="img"
      aria-label={p.nombre}
      style={{
        width,
        height,
        maxWidth: '100%',
        backgroundImage: `url("${src(p.logo.file)}")`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left center',
      }}
    />
  );
}

export function StatusBadge({ estado, size = 'md' }: { estado: Estado | null; size?: 'sm' | 'md' | 'lg' }) {
  if (!estado) return null;
  const dims = { sm: { h: 28, p: '0 12px', f: 13 }, md: { h: 48, p: '0 22px', f: 22 }, lg: { h: 52, p: '0 24px', f: 24 } }[size];
  return (
    // Lima como FONDO con texto azul: permitido. Nunca lima como texto sobre claro.
    <div
      className="flex flex-none items-center whitespace-nowrap rounded-full bg-secundario font-bold text-primario"
      style={{ height: dims.h, padding: dims.p, fontSize: dims.f }}
    >
      {ESTADO_LABEL[estado]}
    </div>
  );
}

export function Arrow({ dir, size = 36 }: { dir: 'left' | 'right'; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={dir === 'right' ? 'M5 12h13M13 6l6 6-6 6' : 'M19 12H6M11 6l-6 6 6 6'}
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ExpandIcon({ size = 30 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PinIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21Z" stroke="currentColor" strokeWidth="2.2" />
      <circle cx="12" cy="9.5" r="2.5" fill="currentColor" />
    </svg>
  );
}
